import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from './AuthContext';
import { FetchContext } from './FetchContext';
import { usePostHog } from '@posthog/react';
import AppUpdateCard from '../components/AppUpdateCard';
import { APP_RELEASE_ID } from '../config/appRelease';

const HeartbeatContext = createContext();

const HEARTBEAT_INTERVAL_MS = 30 * 1000; // 30 seconds
const normalizeReleaseId = (value) => String(value ?? '').trim();

const reloadToLatestVersion = () => {
    try {
        const url = new URL(window.location.href);
        url.searchParams.set('__reload', String(Date.now()));
        window.location.replace(url.toString());
        return;
    } catch (_) {}

    window.location.reload();
};

const isTabActiveInBrowser = () => {
    try {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
            return false;
        }
        if (typeof document !== 'undefined' && typeof document.hasFocus === 'function' && !document.hasFocus()) {
            return false;
        }
    } catch (_) {
        return false;
    }

    return true;
};

const HeartbeatProvider = ({ children }) => {
    const authContext = useContext(AuthContext);
    const fetchContext = useContext(FetchContext);
    const posthog = usePostHog();
    const intervalRef = useRef(null);
    const replayLinkedSessionIdsRef = useRef(new Set());
    const replayLinkInFlightSessionIdsRef = useRef(new Set());
    const [updateRequiredReleaseId, setUpdateRequiredReleaseId] = useState('');
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const [dismissedReleaseId, setDismissedReleaseId] = useState('');
    const clientReleaseId = normalizeReleaseId(APP_RELEASE_ID);

    const getReplayUrl = useCallback(() => {
        if (!posthog || typeof posthog.get_session_replay_url !== 'function') return '';
        try {
            const withTimestamp = posthog.get_session_replay_url({
                withTimestamp: true,
                timestampLookBack: 60,
            });
            if (typeof withTimestamp === 'string' && withTimestamp.trim() !== '') {
                return withTimestamp.trim();
            }
        } catch (_) {}

        try {
            const fallback = posthog.get_session_replay_url();
            if (typeof fallback === 'string' && fallback.trim() !== '') {
                return fallback.trim();
            }
        } catch (_) {}

        return '';
    }, [posthog]);

    const linkReplayForSession = useCallback(async ({ sessionId, token }) => {
        if (!sessionId || !token) return;
        if (replayLinkedSessionIdsRef.current.has(sessionId)) return;
        if (replayLinkInFlightSessionIdsRef.current.has(sessionId)) return;

        const replayUrl = getReplayUrl();
        if (!replayUrl) return;

        replayLinkInFlightSessionIdsRef.current.add(sessionId);
        try {
            await fetchContext.authAxios.post('/session/replay-link', {
                sessionId,
                replayUrl,
                provider: 'posthog',
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            replayLinkedSessionIdsRef.current.add(sessionId);
        } catch (_) {
            // Best-effort only.
        } finally {
            replayLinkInFlightSessionIdsRef.current.delete(sessionId);
        }
    }, [fetchContext, getReplayUrl]);

    const sendHeartbeat = useCallback(async () => {
        try {
            const token = authContext.getToken();
            if (!token) return;

            const response = await fetchContext.authAxios.post('/heartbeat', {
                clientReleaseId,
                tabActive: isTabActiveInBrowser(),
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const sessionId = response?.data?.session_id || response?.data?.sessionId;
            const requiredReleaseId = normalizeReleaseId(
                response?.data?.required_client_release_id ??
                response?.data?.requiredClientReleaseId
            );
            const clientOutdated = Boolean(
                response?.data?.client_outdated ??
                response?.data?.clientOutdated
            );

            setUpdateRequiredReleaseId(requiredReleaseId);
            setIsUpdateAvailable(clientOutdated);
            if (!clientOutdated) {
                setDismissedReleaseId('');
            }

            if (sessionId) {
                await linkReplayForSession({ sessionId, token });
            }
        } catch (error) {
            // Silently fail - heartbeat is best-effort
            // Don't log to avoid console spam
        }
    }, [authContext, clientReleaseId, fetchContext, linkReplayForSession]);

    useEffect(() => {
        const isAuthenticated = authContext.isUserAuthenticated();

        if (isAuthenticated) {
            // Send initial heartbeat immediately
            sendHeartbeat();

            // Set up interval for subsequent heartbeats
            intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [authContext.authState, authContext, sendHeartbeat]); // Re-run when auth state changes

    useEffect(() => {
        if (!posthog) return;

        const isAuthenticated = authContext.isUserAuthenticated();
        if (!isAuthenticated) {
            replayLinkedSessionIdsRef.current.clear();
            replayLinkInFlightSessionIdsRef.current.clear();
            posthog.reset();
            return;
        }

        const userId = authContext.getUserId?.();
        const userEmail = authContext.getUserEmail?.();
        if (!userId) return;

        try {
            posthog.identify(userId, userEmail ? { email: userEmail } : undefined);
        } catch (_) {
            // Best-effort only.
        }
    }, [posthog, authContext.authState, authContext]);

    useEffect(() => {
        const isAuthenticated = authContext.isUserAuthenticated();
        if (!isAuthenticated) return undefined;

        const handleWindowFocus = () => {
            void sendHeartbeat();
        };
        const handleWindowBlur = () => {
            void sendHeartbeat();
        };
        const handleVisibilityChange = () => {
            void sendHeartbeat();
        };

        window.addEventListener('focus', handleWindowFocus);
        window.addEventListener('blur', handleWindowBlur);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleWindowFocus);
            window.removeEventListener('blur', handleWindowBlur);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [authContext, sendHeartbeat]);

    const isUpdateCardVisible = isUpdateAvailable && updateRequiredReleaseId !== dismissedReleaseId;

    const dismissUpdateCard = useCallback(() => {
        if (!updateRequiredReleaseId) return;
        setDismissedReleaseId(updateRequiredReleaseId);
    }, [updateRequiredReleaseId]);

    const contextValue = useMemo(() => ({
        updateAvailable: isUpdateCardVisible,
        requiredReleaseId: updateRequiredReleaseId,
        clientReleaseId,
        dismissUpdateCard,
        reloadToLatestVersion,
    }), [clientReleaseId, dismissUpdateCard, isUpdateCardVisible, updateRequiredReleaseId]);

    return (
        <HeartbeatContext.Provider value={contextValue}>
            {children}
            <AppUpdateCard
                visible={isUpdateCardVisible}
                onDismiss={dismissUpdateCard}
                onReload={reloadToLatestVersion}
            />
        </HeartbeatContext.Provider>
    );
};

export { HeartbeatProvider, HeartbeatContext };
